<template>
  <SoAlert
    v-if="query?.isError"
    type="error"
    title="Failed to load configuration history"
    data-testid="service-config-history-error"
  />

  <SoDataTableServer
    v-else
    :headers="headers"
    :data="tableData"
    :searchable="false"
    :default-items-per-page="50"
    data-testid="service-config-history-table"
    @fetch="fetchData"
  >
    <template #item.isActive="{ internalItem }">
      <ServiceConfigStatusCell
        :active="internalItem.raw.isActive"
        :testid="`service-config-history-status-${internalItem.raw.id}`"
      />
    </template>

    <template #item.start="{ internalItem }">
      <ServiceConfigDateCell
        :display="internalItem.raw.startDateDisplay"
        :tooltip="internalItem.raw.startDisplay"
        :has-value="!!internalItem.raw.start"
      />
    </template>

    <template #item.end="{ internalItem }">
      <ServiceConfigDateCell
        :display="internalItem.raw.endDateDisplay"
        :tooltip="internalItem.raw.endDisplay"
        :has-value="!!internalItem.raw.end"
      />
    </template>

    <template #item.createdAt="{ internalItem }">
      <ServiceConfigDateCell
        :display="internalItem.raw.createdAtDateDisplay"
        :tooltip="internalItem.raw.createdAtDisplay"
      />
    </template>

    <template #item.updatedAt="{ internalItem }">
      <ServiceConfigDateCell
        :display="internalItem.raw.updatedAtDateDisplay"
        :tooltip="internalItem.raw.updatedAtDisplay"
        :has-value="!!internalItem.raw.updatedAt"
      />
    </template>

    <template #item.deletedAt="{ internalItem }">
      <ServiceConfigDateCell
        :display="internalItem.raw.deletedAtDateDisplay"
        :tooltip="internalItem.raw.deletedAtDisplay"
        :has-value="!!internalItem.raw.deletedAt"
      />
    </template>
  </SoDataTableServer>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import ServiceConfigStatusCell from "@/views/admin/serviceConfig/components/ServiceConfigStatusCell.vue"
  import ServiceConfigDateCell from "@/views/admin/serviceConfig/components/ServiceConfigDateCell.vue"
  import { dateTimeColumn } from "@/views/admin/serviceConfig/components/serviceConfigColumns"
  import {
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import {
    ServiceConfigService,
    ConfigHistoryResult,
  } from "@/services/admin/ServiceConfigService"
  import { QueryResult } from "@/api/utils/types"

  // History is paginated server-side and always ordered newest-first, so the
  // columns are not client-sortable (sorting would only reorder the current
  // page, not the full result set).
  const dateColumn = (title: string, key: string): SoDataTableHeader => ({
    ...dateTimeColumn(title, key),
    sortable: false,
  })

  const headers: SoDataTableHeader[] = [
    { title: "Status", key: "isActive", sortable: false },
    { title: "Value", key: "value", sortable: false },
    dateColumn("Start", "start"),
    dateColumn("End", "end"),
    dateColumn("Created", "createdAt"),
    dateColumn("Updated", "updatedAt"),
    dateColumn("Deleted", "deletedAt"),
  ]

  export default defineComponent({
    components: {
      SoAlert,
      SoDataTableServer,
      ServiceConfigStatusCell,
      ServiceConfigDateCell,
    },
    props: {
      configName: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        headers,
        query: null as QueryResult<ConfigHistoryResult> | null,
      }
    },
    computed: {
      tableData(): SoDataTableData {
        return {
          isLoading: !!this.query?.isFetching,
          totalItems: this.query?.data?.totalCount ?? 0,
          items: (this.query?.data?.records ?? []) as SoDataTableItem[],
        }
      },
    },
    methods: {
      fetchData({ page, pageSize }: SoDataTableOptions) {
        this.query = ServiceConfigService.getHistory(
          this.configName,
          page,
          pageSize,
        )
      },
    },
  })
</script>
